import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import "../assets/styles/Webadminprofile.css";
import LeftNav from "../components/common/LeftNav";
import TopNav from "../components/common/TopNav";
import arrowleftt from "../assets/img/arrowleftt.svg";
import authService from "../../services/authService";

// Role types matching AdminProfileScreen.js
const ADMIN_STAFF_TYPES = ['customer_support', 'marketing', 'dashboard_view', 'accounts'];

export default function Webadminprofile() {
  const navigate = useNavigate();
  const [isLoggingOut, setIsLoggingOut] = useState(false);
  const [showLogoutConfirmation, setShowLogoutConfirmation] = useState(false);
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [permissions, setPermissions] = useState({});
  const [effectiveRole, setEffectiveRole] = useState('admin');

  // Load user data on component mount - matching AdminProfileScreen.js
  useEffect(() => {
    loadUserData();
  }, []);

  const loadUserData = async () => {
    try {
      setLoading(true);
      const currentUser = await authService.getCurrentUser();

      // Get user data from localStorage (same as LeftNav.js)
      const userData = localStorage.getItem('user');
      if (userData) {
        const user = JSON.parse(userData);
        // Use admin_user_type from permissions if available, otherwise use role
        const role = user.permissions?.admin_user_type || user.role || 'admin';
        setEffectiveRole(role);
        console.log('Webadminprofile: Effective role determined:', role);
      } else {
        // Fallback: try userRole from localStorage
        const storedRole = localStorage.getItem('userRole');
        setEffectiveRole(storedRole || 'admin');
      }

      if (currentUser) {
        setUser(currentUser);
        const userPermissions = currentUser.permissions || {};
        setPermissions(userPermissions);
      }
    } catch (error) {
      console.error('Error loading user data:', error);
    } finally {
      setLoading(false);
    }
  };

  // Helper function to check if user is super admin (not staff sub-type)
  const isAdmin = () => {
    return effectiveRole === 'admin';
  };

  // Check if user is an admin staff member (customer_support, marketing, etc.)
  const isStaffUser = () => {
    return ADMIN_STAFF_TYPES.includes(effectiveRole);
  };

  // Helper function to check specific permission
  const hasPermission = (permissionKey) => {
    // Admins have all permissions
    if (isAdmin()) return true;
    // Check specific permission
    return permissions[permissionKey] === true;
  };

  // Get user type display - matching AdminProfileScreen.js
  const getUserTypeInfo = () => {
    const role = (effectiveRole || '').toLowerCase();

    const roleMap = {
      'admin': { label: 'Administrator', bgColor: '#2563EB1A', textColor: '#2563EB' },
      'manager': { label: 'Manager', bgColor: '#FACC151A', textColor: '#FACC15' },
      'customer_support': { label: 'Customer Support', bgColor: '#4995FF1A', textColor: '#4995FF' },
      'support': { label: 'Customer Support', bgColor: '#4995FF1A', textColor: '#4995FF' },
      'marketing': { label: 'Marketing', bgColor: '#FACC151A', textColor: '#FACC15' },
      'dashboard_view': { label: 'Dashboard View', bgColor: '#6C5DD31A', textColor: '#6C5DD3' },
      'accounts': { label: 'Accounts', bgColor: '#10B9811A', textColor: '#10B981' },
      'operator': { label: 'Operator', bgColor: '#6C5DD31A', textColor: '#6C5DD3' },
      'sub_user': { label: 'Sub User', bgColor: '#FF754C1A', textColor: '#FF754C' },
      'city_manager': { label: 'City Manager', bgColor: '#10B9811A', textColor: '#10B981' },
    };

    return roleMap[role] || { label: role || 'User', bgColor: '#6B72801A', textColor: '#6B7280' };
  };

  // Menu items configuration based on permissions - matching AdminProfileScreen.js
  const getMenuItems = () => {
    const items = [];

    // Staff users (customer_support, marketing, dashboard_view, accounts) only get Change Password
    if (isStaffUser()) {
      items.push({
        title: 'Change Password',
        path: '/AdminChangePassword',
        icon: 'lock-closed-outline',
      });
      return items;
    }

    // Below items are for super admin only

    // Billing Summary
    items.push({
      title: 'Billing Summary & Invoices',
      path: '/Adminbillingsummary',
      icon: 'receipt-outline',
    });

    // Manage Users
    items.push({
      title: 'Manage Users',
      path: '/Webadminmanageuser',
      icon: 'people-outline',
    });

    // Manage Subscription
    items.push({
      title: 'Manage Subscriptions',
      path: '/Adminwebsubscriptionmonthly',
      icon: 'card-outline',
    });
    items.push({
      title: 'Configuration',
      path: '/AdminConfig',
      icon: 'help-circle-outline',
    });
    // Change Password
    items.push({
      title: 'Change Password',
      path: '/AdminChangePassword',
      icon: 'lock-closed-outline',
    });

    // Help & Support
    items.push({
      title: 'Help & Support Settings',
      path: '/AdminHelpSupport',
      icon: 'help-circle-outline',
    });

    return items;
  };

  const menuItems = getMenuItems();

  const handleLogout = () => {
    setShowLogoutConfirmation(true);
  };

  const confirmLogout = async () => {
    setIsLoggingOut(true);
    setShowLogoutConfirmation(false);

    try {
      console.log('🔄 Logging out user...');

      // Call logout service which clears tokens and calls API
      await authService.logout();

      console.log('✅ Logout successful');

      // Navigate to login page
      navigate('/AdminLogin', { replace: true });
    } catch (error) {
      console.error('❌ Logout error:', error);

      // Even if logout API fails, clear local storage and redirect
      localStorage.clear();
      navigate('/login', { replace: true });
    } finally {
      setIsLoggingOut(false);
    }
  };

  const cancelLogout = () => {
    setShowLogoutConfirmation(false);
  };

  // Show loading state while fetching user data
  if (loading) {
    return (
      <div className="d-flex">
        <LeftNav />
        <div className="main-content flex-grow-1">
          <TopNav />
          <div className="p-4">
            <div style={{ border: "1px solid #D6D6D6A3", borderRadius: "10px" }} className="p-4">
              <div className="text-center py-5">
                <div className="spinner-border text-primary" role="status">
                  <span className="visually-hidden">Loading...</span>
                </div>
                <div className="mt-3" style={{ color: '#888' }}>Loading...</div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const userTypeInfo = getUserTypeInfo();

  return (
    <div className="d-flex">
      <LeftNav />
      <div className="main-content flex-grow-1">
        <TopNav />
        <div className="p-4">
          <div style={{ border: "1px solid #D6D6D6A3", borderRadius: "10px" }} className="p-4">
            {/* Header */}
            <div className="profile-header">
              <button className="profile-back-btn" onClick={() => navigate(-1)}>
                <img src={arrowleftt} alt="Back"></img>
              </button>
              <h1 className="profile-title">Account Settings</h1>
            </div>

            {/* User Profile Section - matching AdminProfileScreen.js */}
            <div className="profile-card" style={{
              backgroundColor: '#fff',
              borderRadius: '16px',
              padding: '20px',
              marginBottom: '16px',
              boxShadow: '0 2px 8px rgba(0,0,0,0.05)',
              border: '1px solid #E5E7EB'
            }}>
              <div className="profile-header-section" style={{
                display: 'flex',
                alignItems: 'center',
                marginBottom: '16px'
              }}>
                <div className="avatar-container" style={{ marginRight: '16px' }}>
                  {user?.profile?.avatar_url ? (
                    <img
                      src={user.profile.avatar_url}
                      alt="Avatar"
                      style={{
                        width: '70px',
                        height: '70px',
                        borderRadius: '35px'
                      }}
                    />
                  ) : (
                    <div style={{
                      width: '70px',
                      height: '70px',
                      borderRadius: '35px',
                      backgroundColor: '#2563EB',
                      display: 'flex',
                      justifyContent: 'center',
                      alignItems: 'center'
                    }}>
                      <span style={{
                        color: '#fff',
                        fontSize: '28px',
                        fontWeight: 'bold'
                      }}>
                        {(user?.profile?.full_name || user?.email || 'U')[0].toUpperCase()}
                      </span>
                    </div>
                  )}
                </div>
                <div className="profile-info" style={{ flex: 1 }}>
                  <h2 style={{
                    fontSize: '20px',
                    fontWeight: '700',
                    color: '#000',
                    marginBottom: '4px',
                    margin: 0
                  }}>
                    {user?.profile?.full_name ||
                      `${user?.profile?.first_name || ''} ${user?.profile?.last_name || ''}`.trim() ||
                      'User'}
                  </h2>
                  <p style={{
                    fontSize: '14px',
                    color: '#6B7280',
                    margin: '4px 0 6px 0'
                  }}>
                    {user?.email || ''}
                  </p>
                  <div style={{
                    backgroundColor: userTypeInfo.bgColor,
                    paddingTop: '4px',
                    paddingBottom: '4px',
                    paddingLeft: '12px',
                    paddingRight: '12px',
                    borderRadius: '12px',
                    display: 'inline-block',
                    marginTop: '6px',
                  }}>
                    <span style={{
                      fontWeight: '600',
                      color: userTypeInfo.textColor,
                      fontSize: '12px',
                    }}>
                      {userTypeInfo.label}
                    </span>
                  </div>
                </div>
              </div>

              {/* Permissions Display - Only for staff users */}
              {isStaffUser() && (
                <div style={{
                  marginTop: '16px',
                  paddingTop: '16px',
                  borderTop: '1px solid #E5E7EB'
                }}>
                  <p style={{
                    fontSize: '14px',
                    fontWeight: '600',
                    color: '#374151',
                    marginBottom: '12px'
                  }}>
                    Your Role:
                  </p>
                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    backgroundColor: '#F3F4F6',
                    paddingLeft: '12px',
                    paddingRight: '12px',
                    paddingTop: '6px',
                    paddingBottom: '6px',
                    borderRadius: '16px',
                    gap: '6px',
                    width: 'fit-content'
                  }}>
                    <span style={{ fontSize: '14px' }}>🛡️</span>
                    <span style={{
                      fontSize: '12px',
                      fontWeight: '500',
                      color: '#374151',
                    }}>
                      {userTypeInfo.label}
                    </span>
                  </div>
                </div>
              )}
            </div>

            {/* Settings Card */}
            <div className="profile-settings-card">
              {/* Menu Items */}
              {menuItems.map((item, index) => (
                <div
                  key={index}
                  className="profile-menu-item"
                  onClick={() => navigate(item.path)}
                >
                  <span className="profile-menu-text">{item.title}</span>
                  <span className="profile-menu-arrow">&#8250;</span>
                </div>
              ))}

              {/* Logout Button */}
              <button
                className="profile-logout-btn"
                onClick={handleLogout}
                disabled={isLoggingOut}
              >
                {isLoggingOut ? 'Logging out...' : 'Logout'}
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Logout Confirmation Modal */}
      {showLogoutConfirmation && (
        <div className="modal fade show" style={{ display: 'block', backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Confirm Logout</h5>
                <button type="button" className="btn-close" onClick={cancelLogout}></button>
              </div>
              <div className="modal-body">
                <p>Are you sure you want to logout?</p>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={cancelLogout}>
                  Cancel
                </button>
                <button
                  type="button"
                  className="btn btn-danger"
                  onClick={confirmLogout}
                  disabled={isLoggingOut}
                >
                  {isLoggingOut ? 'Logging out...' : 'Logout'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
